import { GridReadyEvent, ValueSetterParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AgFns, BaseService, GridOptionsExt, ISortModel, SaveAndQueryComponent } from '@core';
import {
  Account,
  NotificationEvent,
  NotificationEventAccountMap,
  NotificationEventEnum,
  NotificationType,
  NotificationTypeEnum,
} from '@models';
import { UtilFns } from '@utils';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { EntityFns } from '@data';

type NotificationTypeMap = {
  [key in NotificationTypeEnum]?: NotificationEventAccountMap;
};

interface EventSummary extends NotificationTypeMap {
  notificationEvent: NotificationEvent;
}

// Email wrapper events only support Email notification type (not SMS or InApp)
const EMAIL_ONLY_EVENT_IDS = [NotificationEventEnum.EmailHeader, NotificationEventEnum.EmailFooter];

@Component({
  selector: 'prox-account-notification-settings',
  templateUrl: './account-notification-settings.component.html',
  standalone: false,
})
export class AccountNotificationSettingsComponent extends SaveAndQueryComponent {
  accountId!: string;
  account?: Account;
  notificationEvents: NotificationEvent[] = [];
  notificationTypes: NotificationType[] = [];
  accountMaps: NotificationEventAccountMap[] = [];
  gridOptions: GridOptionsExt = {};
  eventSummaries: EventSummary[] = [];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    public override dbQueryService: AccountDbQueryService,
    public override dbSaveService: AccountDbSaveService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);

    // this.templates = this.getComms();
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,

      getRowId: undefined, // couldn't refresh rows without setting this
    });

    this.notificationEvents = await this.dbQueryService.getAll(NotificationEvent);
    this.notificationTypes = await this.dbQueryService.getAll(NotificationType);
    await this.loadMaps();

    AgFns.captureGridRouteParams(this.gridOptions, this.route, 'id');
    this.setTitle('Notification Settings for ' + this.account.name);
    this.isPageReady = true;
  }

  private async loadMaps() {
    const eventMap = new Map<NotificationEventEnum, EventSummary>();
    // DEPRECATED 2026-05-15: Email Header / Footer rows are hidden from this grid.
    // The Cerberus shell (server/Proximity.Api/Helpers/EmailTemplate.cs) now provides
    // the only email header/footer; admins no longer need to configure them per account.
    // The EMAIL_ONLY_EVENT_IDS constant and surrounding restriction logic is kept intact
    // so the UI can be restored cleanly if needed.
    this.eventSummaries = this.notificationEvents
      .filter((ne) => !EMAIL_ONLY_EVENT_IDS.includes(ne.id))
      .map((ne) => {
        const eventSummary = <EventSummary>{
          notificationEvent: ne,
        };
        eventMap.set(ne.id, eventSummary);
        return eventSummary;
      });
    this.accountMaps = await this.dbQueryService.getNotificationEventAccountMaps(this.accountId);

    this.accountMaps.forEach((map) => {
      const eventSummary = eventMap.get(map.notificationEventId);
      if (eventSummary) {
        eventSummary[map.notificationTypeId] = map;
      }
    });
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Notification Event',
        field: 'notificationEvent.description',
        sortable: true,
        ...AgFns.flexWidth(400),
      },
      this.createNtProps(NotificationTypeEnum.Email),
      this.createNtProps(NotificationTypeEnum.SMS),
      this.createNtProps(NotificationTypeEnum.InApp),
    ];

    const sortModel: ISortModel = [{ colId: 'notificationEvent.description', sort: 'asc' }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  createNtProps(notificationTypeId: NotificationTypeEnum) {
    // Check if this notification type should be disabled for email wrapper events
    const isEmailOnlyRestricted = notificationTypeId !== NotificationTypeEnum.Email;

    return {
      headerName: NotificationTypeEnum[notificationTypeId],
      cellEditor: 'agCheckboxCellEditor',
      cellRenderer: 'agCheckboxCellRenderer',
      valueGetter: (params) => {
        const eventSummary = params.data as EventSummary;
        if (eventSummary == null) return;

        // Email Header/Footer only support Email - always show unchecked for SMS/InApp
        if (isEmailOnlyRestricted && EMAIL_ONLY_EVENT_IDS.includes(eventSummary.notificationEvent.id)) {
          return false;
        }

        return eventSummary[notificationTypeId] != null;
      },
      valueSetter: (params: ValueSetterParams) => {
        const eventSummary = params.data as EventSummary;
        let accountMap = eventSummary[notificationTypeId];
        if (accountMap == null) {
          if (params.newValue) {
            accountMap = this.dbSaveService.createEntity(NotificationEventAccountMap, {
              notificationEventId: eventSummary.notificationEvent.id,
              notificationTypeId: notificationTypeId,
              accountId: this.accountId,
            });
            eventSummary[notificationTypeId] = accountMap;
          }
        } else {
          if (params.newValue) {
            accountMap.entityAspect.rejectChanges();
          } else {
            const es = EntityFns.deleteOrDetach(accountMap.entityAspect);
            if (es.isDetached()) {
              eventSummary[notificationTypeId] = undefined;
            }
          }
        }

        return true;
      },
      sortable: true,
      editable: (params: { data: EventSummary }) => {
        // Email Header/Footer only support Email - disable SMS/InApp
        if (isEmailOnlyRestricted) {
          const eventSummary = params.data;
          if (EMAIL_ONLY_EVENT_IDS.includes(eventSummary.notificationEvent.id)) {
            return false;
          }
        }
        return true;
      },
      cellStyle: (params: { data: EventSummary }) => {
        // Grey background for disabled SMS/InApp cells on Email Header/Footer rows
        if (isEmailOnlyRestricted && EMAIL_ONLY_EVENT_IDS.includes(params.data?.notificationEvent?.id)) {
          return { backgroundColor: '#f0f0f0' };
        }
        return null;
      },
      width: 250,
      maxWidth: 500,
    };
  }

  onRowSelected() {}

  override async afterUndo() {
    // Reload the maps to restore original state and refresh the grid
    await this.loadMaps();
    this.gridOptions.apiX?.setGridOption('rowData', this.eventSummaries);
  }
}
